import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, X, Info, ShieldCheck, BadgeCheck, BookmarkCheck, Sprout, FileCheck2 } from 'lucide-react';
import { pointerMotion } from '../../lib/interactionFeedback';

const ICONS = { resolved: ShieldCheck, validated: BadgeCheck, decision: BookmarkCheck, learned: Sprout, evidence: FileCheck2 };

export function Toast({ message, type = 'info', duration = 4000, onClose, reaction, detail, motion = true }) {
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (leaving || paused || !duration) return;
    const start = Date.now();
    const timer = setTimeout(() => setLeaving(true), remaining.current);
    return () => { clearTimeout(timer); remaining.current = Math.max(0, remaining.current - (Date.now() - start)); };
  }, [duration, paused, leaving]);
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => close.current(), motion ? 180 : 0);
    return () => clearTimeout(timer);
  }, [leaving, motion]);
  useEffect(() => {
    const hidden = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', hidden);
    return () => document.removeEventListener('visibilitychange', hidden);
  }, []);
  const Icon = ICONS[reaction] || (type === 'success' ? CheckCircle2 : type === 'error' ? AlertCircle : Info);
  return <div className={`toast toast-${type}${reaction ? ' toast-reaction' : ''}${leaving ? ' leaving' : ''}`}
    data-reaction={reaction} data-motion={motion} role={type === 'error' ? 'alert' : 'status'}
    onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
    onFocus={() => setPaused(true)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false); }}>
    <div className="toast-icon" aria-hidden="true"><Icon size={reaction ? 22 : 18}/></div>
    <div className="toast-body">{reaction ? <strong>{message}</strong> : message}{detail && <span className="toast-detail">{detail}</span>}</div>
    <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => setLeaving(true)} aria-label="Dismiss notification"><X size={14}/></button>
  </div>;
}

// All existing page toasts and action reactions share one stack, even across routes.
export function ToastContainer({ toasts, removeToast }) {
  if (!toasts.length) return null;
  let host = document.getElementById('spcc-toast-stack');
  if (!host) { host = document.createElement('div'); host.id = 'spcc-toast-stack'; host.className = 'toast-stack'; document.body.appendChild(host); }
  return createPortal(toasts.map(toast => <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)}/>), host);
}

export function useToast() {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((message, type = 'info', duration = 4000, options = {}) => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev.slice(-2), { id, message, type, duration, motion: pointerMotion(), ...options }]);
    return id;
  }, []);
  const removeToast = useCallback(id => setToasts(prev => prev.filter(toast => toast.id !== id)), []);
  const clearToasts = useCallback(() => setToasts([]), []);
  const showSuccess = useCallback((message, duration) => addToast(message, 'success', duration), [addToast]);
  const showError = useCallback((message, duration) => addToast(message, 'error', duration), [addToast]);
  const showInfo = useCallback((message, duration) => addToast(message, 'info', duration), [addToast]);
  return { toasts, addToast, removeToast, clearToasts, showSuccess, showError, showInfo };
}
